import { useRef, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * Hook para gerenciar notificações de forma centralizada
 * Evita múltiplas notificações do mesmo tipo em um período curto
 */
export const useNotificationManager = () => {
  const notificationHistory = useRef(new Map());
  const COOLDOWN_TIME = 2000; // 2 segundos

  const showNotification = useCallback((type, message, options = {}) => {
    const now = Date.now();
    // Criar uma chave mais específica incluindo a descrição se disponível
    const description = options.description || '';
    const key = `${type}-${message}-${description}`;
    
    // Verificar se já mostrou esta notificação recentemente
    const lastShown = notificationHistory.current.get(key);
    if (lastShown && (now - lastShown) < COOLDOWN_TIME) {
      console.log(`Notificação bloqueada (deduplicação): ${key}`);
      return; // Não mostrar se ainda está no período de cooldown
    }

    // Registrar que esta notificação foi mostrada
    notificationHistory.current.set(key, now);
    console.log(`Notificação exibida: ${key}`);

    // Mostrar a notificação
    if (type === 'success') {
      toast.success(message, options);
    } else if (type === 'error') {
      toast.error(message, options);
    } else if (type === 'info') {
      toast.info(message, options);
    } else if (type === 'warning') {
      toast.warning(message, options);
    }
  }, []);

  const clearHistory = useCallback(() => {
    notificationHistory.current.clear();
  }, []);

  return {
    showSuccess: (message, options) => showNotification('success', message, options),
    showError: (message, options) => showNotification('error', message, options),
    showInfo: (message, options) => showNotification('info', message, options),
    showWarning: (message, options) => showNotification('warning', message, options),
    clearHistory
  };
};
